import { create } from 'zustand'
import { APP_VERSION } from './version'

export type DiagnosticLevel = 'INFO' | 'WARN' | 'ERROR'
export type DiagnosticCategory = 'PLAYER' | 'MEDIA' | 'NETWORK' | 'BUFFER' | 'VIDEO' | 'AUDIO' | 'SYSTEM' | 'ERROR'

export interface DiagnosticEvent {
  id: string
  timestamp: string
  sessionId: string
  level: DiagnosticLevel
  category: DiagnosticCategory
  event: string
  message: string
  data?: Record<string, unknown>
}

export interface DiagnosticSummary {
  player: {
    status: string; title: string | null; position: number; duration: number; playbackRate: number
    volume: number; muted: boolean; fullscreen: boolean; pictureInPicture: boolean
  }
  media: {
    resolution: string | null; videoCodec: string | null; audioCodec: string | null; fps: number | null
    bitrate: number | null; fileSize: number | null; sourceType: string | null; bufferedSeconds: number
  }
  performance: {
    droppedFrames: number; totalFrames: number; stalls: number; totalStallDurationMs: number
    startupTimeMs: number | null; lastSeekDurationMs: number | null; bufferHealth: string
  }
  network: {
    online: boolean; connectionSpeedMbps: number | null; mediaThroughputMbps: number | null
    httpStatus: number | null; requestDurationMs: number | null; failedRequests: number; retries: number; timeouts: number
  }
  system: {
    browser: string; operatingSystem: string; screenResolution: string; playerViewport: string
    devicePixelRatio: number; hardwareDecoding: string
  }
  session: { id: string; appVersion: string; startedAt: string }
}

const startedAt = new Date().toISOString()
const sessionId = `CP-${crypto.getRandomValues(new Uint8Array(3)).reduce((value, byte) => value + byte.toString(16).padStart(2, '0'), '').toUpperCase()}`
const connection = (navigator as Navigator & { connection?: { downlink?: number } }).connection

function browserName() {
  const ua = navigator.userAgent
  const match = ua.match(/(Edg|Chrome|Firefox|Version)\/(\d+(?:\.\d+)?)/)
  const name = match?.[1] === 'Version' ? 'Safari' : match?.[1]
  return name && match ? `${name} ${match[2]}` : navigator.userAgent
}

function operatingSystem() {
  const ua = navigator.userAgent
  if (/Windows NT/.test(ua)) return 'Windows'
  if (/Mac OS X/.test(ua)) return `macOS ${ua.match(/Mac OS X ([\d_]+)/)?.[1]?.replace(/_/g, '.') ?? ''}`.trim()
  if (/Android/.test(ua)) return 'Android'
  if (/iPhone|iPad/.test(ua)) return 'iOS/iPadOS'
  if (/Linux/.test(ua)) return 'Linux'
  return navigator.platform || 'Unknown'
}

const initialSummary: DiagnosticSummary = {
  player: { status: 'Idle', title: null, position: 0, duration: 0, playbackRate: 1, volume: 1, muted: false, fullscreen: false, pictureInPicture: false },
  media: { resolution: null, videoCodec: null, audioCodec: null, fps: null, bitrate: null, fileSize: null, sourceType: null, bufferedSeconds: 0 },
  performance: { droppedFrames: 0, totalFrames: 0, stalls: 0, totalStallDurationMs: 0, startupTimeMs: null, lastSeekDurationMs: null, bufferHealth: 'Empty' },
  network: { online: navigator.onLine, connectionSpeedMbps: connection?.downlink ?? null, mediaThroughputMbps: null, httpStatus: null, requestDurationMs: null, failedRequests: 0, retries: 0, timeouts: 0 },
  system: { browser: browserName(), operatingSystem: operatingSystem(), screenResolution: `${screen.width}×${screen.height}`, playerViewport: '—', devicePixelRatio: window.devicePixelRatio, hardwareDecoding: 'Not detectable' },
  session: { id: sessionId, appVersion: APP_VERSION, startedAt },
}

const DB_NAME = 'cloud-player-diagnostics'
const STORE_NAME = 'events'
const RETENTION_KEY = 'cloud-player-diagnostics-retention-days'
export let DIAGNOSTIC_RETENTION_DAYS = Math.max(1, Number(localStorage.getItem(RETENTION_KEY)) || 14)

export function setDiagnosticRetentionDays(days: number) {
  DIAGNOSTIC_RETENTION_DAYS = Math.max(1, Math.min(365, Math.round(days)))
  localStorage.setItem(RETENTION_KEY, String(DIAGNOSTIC_RETENTION_DAYS))
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)
    request.onupgradeneeded = () => {
      const store = request.result.createObjectStore(STORE_NAME, { keyPath: 'id' })
      store.createIndex('timestamp', 'timestamp')
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function persist(event: DiagnosticEvent) {
  try {
    const db = await openDb()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).put(event)
    const cutoff = new Date(Date.now() - DIAGNOSTIC_RETENTION_DAYS * 86_400_000).toISOString()
    const range = IDBKeyRange.upperBound(cutoff, true)
    tx.objectStore(STORE_NAME).index('timestamp').openKeyCursor(range).onsuccess = (cursorEvent) => {
      const cursor = (cursorEvent.target as IDBRequest<IDBCursor | null>).result
      if (cursor) { tx.objectStore(STORE_NAME).delete(cursor.primaryKey); cursor.continue() }
    }
  } catch {
    // Diagnostics must never interfere with playback when storage is unavailable.
  }
}

export async function readDiagnosticEvents(from?: Date, to?: Date): Promise<DiagnosticEvent[]> {
  try {
    const db = await openDb()
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const request = tx.objectStore(STORE_NAME).index('timestamp').getAll(
        from || to ? IDBKeyRange.bound(from?.toISOString() ?? '', to?.toISOString() ?? '\uffff') : undefined,
      )
      request.onsuccess = () => resolve((request.result as DiagnosticEvent[]).sort((a, b) => a.timestamp.localeCompare(b.timestamp)))
      request.onerror = () => reject(request.error)
    })
  } catch {
    return []
  }
}

interface DiagnosticsStore {
  events: DiagnosticEvent[]
  visibleSince: string
  summary: DiagnosticSummary
  append: (event: Omit<DiagnosticEvent, 'id' | 'timestamp' | 'sessionId'>) => void
  updateSummary: (section: keyof DiagnosticSummary, values: Record<string, unknown>) => void
  clearLiveView: () => void
}

export const useDiagnosticsStore = create<DiagnosticsStore>((set) => ({
  events: [],
  visibleSince: startedAt,
  summary: initialSummary,
  append: (input) => {
    const event: DiagnosticEvent = {
      ...input,
      id: `${Date.now()}-${crypto.randomUUID()}`,
      timestamp: new Date().toISOString(),
      sessionId,
    }
    set((state) => ({ events: [...state.events, event].slice(-2000) }))
    void persist(event)
  },
  updateSummary: (section, values) => set((state) => ({
    summary: { ...state.summary, [section]: { ...state.summary[section], ...values } },
  })),
  clearLiveView: () => set({ visibleSince: new Date().toISOString() }),
}))

export function logDiagnostic(
  level: DiagnosticLevel,
  category: DiagnosticCategory,
  event: string,
  message: string,
  data?: Record<string, unknown>,
) {
  useDiagnosticsStore.getState().append({ level, category, event, message, data })
}

logDiagnostic('INFO', 'SYSTEM', 'SESSION_STARTED', 'Player initialized', { appVersion: APP_VERSION, retentionDays: DIAGNOSTIC_RETENTION_DAYS })
